<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import config from "@/config";
import RvApi from "@/rvapi";
import type {
  Rules,
  Title,
  Level,
  Platform,
  View,
  DateRule,
  VersionRule,
  PlatformMessages,
} from "@/rvapi";
import { timezone, local2utc, utc2local } from "@/utils";

const route = useRoute();
const router = useRouter();
const loading = ref(true);
const action = ref("Publish");
const platformHeader = ref("");
onMounted(async () => {
  const messageid = route.params.id as string;
  const platform = route.params.platform as string;

  if (messageid && platform) {
    platformHeader.value = platform;
    const url = `${
      config.systemStatusBucket
    }/${platform}.json?${Math.random().toString()}`;
    const messages: PlatformMessages = await RvApi.raw(url);
    const filtered = messages.info
      .concat(messages.critical)
      .filter((m) => m.id === messageid);
    if (filtered.length === 0) {
      throw "could not find message id " + messageid;
    }
    const message = filtered[0];
    english.value = message.title?.en;
    swedish.value = message.title?.sv ?? "";
    norwegian.value = message.title?.nb ?? "";
    danish.value = message.title?.da ?? "";
    level.value = message.level;
    if (message?.rules.version) {
      versionfrom.value = message.rules.version.from ?? "";
      versionto.value = message.rules.version.from ?? "";
    }
    if (message?.rules.view) {
      views.value = message.rules.view;
    }
    if (message?.rules?.date?.from) {
      const mtf = utc2local(message?.rules?.date?.from);
      console.log(mtf);
      timespanfrom.value = mtf.substring(0, 10);
      timespanfromt.value = mtf.substring(11, 16);
    }
    if (message?.rules?.date?.to) {
      const mtt = utc2local(message?.rules?.date?.to);
      console.log(mtt);
      timespanuntil.value = mtt.substring(0, 10);
      timespanuntilt.value = mtt.substring(11, 16);
    }
    action.value = "Edit";
  }
  loading.value = false;
});

const emit = defineEmits<{
  (eventName: "refresh", platforms: string[]): void;
}>();

const posting = ref(false);

const viewoptions = [
  { id: "start" as View },
  { id: "cart" as View },
  { id: "checkout" as View },
];
const leveloptions = [
  { id: "info" as Level, label: "Info" },
  { id: "critical" as Level, label: "Critical" },
];
const platformoptions = [
  { id: "web" as Platform, label: "Web" },
  { id: "ios" as Platform, label: "iOS" },
  { id: "android" as Platform, label: "Android" },
];
const english = ref("");
const swedish = ref("");
const norwegian = ref("");
const danish = ref("");

const versionfrom = ref("");
const versionto = ref("");

const timespanfrom = ref<string | null>(null);
const timespanuntil = ref<string | null>(null);
const timespanfromt = ref("");
const timespanuntilt = ref("");
const level = ref("info" as Level);
const views = ref<View[]>(["start", "cart", "checkout"]);
const platforms = ref<Platform[]>(["ios", "android", "web"]);

const getTitle = (): Title => {
  const title: Title = {
    en: english.value, //en is required
  };
  if (swedish.value) {
    title["sv"] = swedish.value;
  }
  if (danish.value) {
    title["da"] = danish.value;
  }
  if (norwegian.value) {
    title["nb"] = norwegian.value;
  }
  return title;
};

const canPost = computed(() => {
  return views.value.length > 0 && platforms.value.length > 0 && english.value;
});

const publish = async () => {
  posting.value = true;

  const rules: Rules = {};

  if (views.value.length > 0) {
    rules.view = views.value;
  }

  //@ts-ignore
  if (timespanuntil.value || timespanfrom.value) {
    rules.date = {} as DateRule;
    if (timespanuntil.value) {
      rules.date.to = local2utc(timespanuntil.value, timespanuntilt.value);
    }
    //@ts-ignore
    if (timespanfrom.value) {
      rules.date.from = local2utc(
        //@ts-ignore
        timespanfrom.value,
        timespanfromt.value,
      );
    }
  }

  if (versionfrom.value || versionto.value) {
    rules.version = {} as VersionRule;
    if (versionfrom.value) {
      rules.version.from = versionfrom.value;
    }
    if (versionto.value) {
      rules.version.to = versionto.value;
    }
  }

  if (action.value === "Edit") {
    const message = {
      platform: route.params.platform as Platform,
      level: level.value,
      title: getTitle(),
      rules: rules,
    };
    await RvApi.updateSystemStatusMessage(message, route.params.id as string);
    router.push({ name: "broadcast" });
  } else {
    const messages = platforms.value.map((p) => {
      return {
        platform: p,
        level: level.value,
        title: getTitle(),
        rules: rules,
      };
    });
    await RvApi.createSystemStatusMessage({ items: messages });
  }
  emit("refresh", platforms.value as string[]);
  timespanuntil.value = null;
  timespanfrom.value = null;
  level.value = "info";
  english.value = swedish.value = norwegian.value = danish.value = "";
  views.value = ["cart", "checkout", "start"];
  platforms.value = ["ios", "android", "web"];
  posting.value = false;
};

const fromDateSelected = (date: { detail: { start: string } }) => {
  timespanfrom.value = date.detail?.start;
};

const untilDateSelected = (date: { detail: { start: string } }) => {
  timespanuntil.value = date.detail?.start;
};
</script>

<template>
  <h2>{{ action }} message {{ platformHeader }}</h2>
  <pn-spinner size="4" v-show="loading" />
  <form v-show="!loading" id="publish-message">
    <h3>Level</h3>
    <label v-for="l in leveloptions" :key="l.id" :for="l.id" :class="l.id">
      <pn-radio-button
        :radioid="l.id"
        name="level"
        :value="l.id"
        @change="level = l.id"
        :checked="l.id === level"
      />
      {{ l.label }}
    </label>

    <h3>Messages</h3>
    <label class="dist"> English (default) </label>
    <pn-input name="english" v-model="english" />
    <label class="dist"> Swedish </label>
    <pn-input name="swedish" v-model="swedish" />
    <label class="dist"> Danish </label>
    <pn-input name="danish" v-model="danish" />
    <label class="dist"> Norwegian </label>
    <pn-input name="norwegian" v-model="norwegian" />

    <h3>Timespan</h3>
    <p>
      Your current timezone is
      {{ timezone() }}. This form will convert from this timezone to UTC.
    </p>
    <div class="flex align-end dateTImeBox">
      <pn-date-picker
        label-from="From"
        :start="timespanfrom"
        @dateSelection="fromDateSelected"
        class="datePicker"
      />
      <input type="time" class="time" v-model="timespanfromt" /><br />
    </div>
    <div class="flex align-end dateTImeBox">
      <pn-date-picker
        label-from="Until"
        :start="timespanuntil"
        @dateSelection="untilDateSelected"
        class="datePicker"
      />
      <input type="time" class="time" v-model="timespanuntilt" />
    </div>
    <p>Leave these blank to always show the message.</p>

    <h3>Show on</h3>
    <div>
      <h4>views</h4>
      <label v-for="v in viewoptions" :key="v.id" :for="v.id">
        <input type="checkbox" :id="v.id" :value="v.id" v-model="views" />
        {{ v.id }}
      </label>
    </div>

    <div v-show="action === 'Publish'">
      <h4>platforms</h4>
      <label v-for="p in platformoptions" :key="p.id" :for="p.id">
        <input type="checkbox" :id="p.id" :value="p.id" v-model="platforms" />
        {{ p.label }}
      </label>
    </div>
    <div class="dist">
      <h4>app versions</h4>
      <div>
        <label class="dist">higher than</label>
        <pn-input class="short" name="english" v-model="versionfrom" />
      </div>
      <div>
        <label class="dist">lower than</label>
        <pn-input class="short" name="english" v-model="versionto" />
      </div>
      <p>Leave these blank to always show the message.</p>
    </div>
    <hr />

    <span style="display: block; margin-top: 2rem">
      <div v-show="action === 'Publish' && canPost">
        <pn-button :loading="posting" @click="publish">Publish</pn-button>
      </div>
      <div v-show="action === 'Edit' && canPost">
        <pn-button :loading="posting" @click="publish">Edit</pn-button>
      </div>
      <pn-toast closable="false" v-show="!canPost">
        Enter a message to enable saving
      </pn-toast>
    </span>
  </form>
</template>

<style lang="scss" scoped>
.short {
  width: 100px;
}
input[type="checkbox"] {
  transform: scale(2);
  filter: saturate(0.4);
  margin-right: 2rem;
  margin-top: 1rem;
}

.datePicker {
  width: 35rem;
}

.dateTImeBox {
  gap: 1rem;
}
</style>
